import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, MapPin, Mail } from "lucide-react";
import { FaFacebookF, FaLinkedinIn, FaInstagram, FaYoutube } from "react-icons/fa6";
import logoImg from "../../assets/logo.webp";

function Footer() {
  const [showFloating, setShowFloating] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowFloating(window.scrollY > 300);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="bg-[#191e1c] text-white">
      <div className="section-container py-14 sm:py-16 lg:py-20">

        {/* ================= 5 COLUMNS MAIN GRID ================= */}
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8 xl:gap-10">

          {/* Column 1: Brand Info & Socials (Width: 3.5 cols) */}
          <div className="sm:col-span-2 lg:col-span-4 xl:col-span-3">
            {/* Logo */}
            <Link
              to="/"
              className="inline-flex items-center gap-2.5"
              aria-label="Finverse Home"
            >
              <img
                src={logoImg}
                alt="Finverse"
                className="h-8 sm:h-9 w-auto object-contain"
                loading="lazy"
              />
              <span className="brand-gradient-text font-jakarta text-[22px] font-bold tracking-[-0.6px]">
                Finverse
              </span>
            </Link>

            {/* Description */}
            <p className="mt-4 font-jakarta text-[14px] leading-[1.65] text-[#9bb0a5] max-w-[320px]">
              Finverse is a digital lending and lead facilitation platform operated by Parthv Capverse Advisory Private Limited.
            </p>

            {/* Social Media Circular Buttons */}
            <div className="mt-6 flex items-center gap-3">
              {/* Facebook */}
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1877f2] text-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
              >
                <FaFacebookF size={17} />
              </a>

              {/* LinkedIn */}
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noreferrer"
                aria-label="LinkedIn"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0077b5] text-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
              >
                <FaLinkedinIn size={17} />
              </a>

              {/* Instagram */}
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-tr from-[#f09433] via-[#e6683c] via-[#dc2743] via-[#cc2366] to-[#bc1888] text-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
              >
                <FaInstagram size={18} />
              </a>

              {/* YouTube */}
              <a
                href="https://youtube.com"
                target="_blank"
                rel="noreferrer"
                aria-label="YouTube"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-[#ff0000] text-white shadow-md transition-all duration-200 hover:scale-110 hover:shadow-lg"
              >
                <FaYoutube size={18} />
              </a>
            </div>
          </div>

          {/* Column 2: Products (Width: 2 cols) */}
          <div className="lg:col-span-2 xl:col-span-2">
            <h3 className="font-bricolage text-[16px] font-bold text-white tracking-wide">
              Products
            </h3>
            <ul className="mt-4 space-y-2.5">
              {[
                { name: "Personal Loan", path: "/loan-products" },
                { name: "Business Loan", path: "/loan-products" },
                { name: "Loan Against Property", path: "/loan-products" },
                { name: "Gold Loan", path: "/loan-products" },
                { name: "EV Loan", path: "/loan-products" },  
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="font-jakarta text-[14px] text-[#9bb0a5] transition-colors duration-200 hover:text-[var(--mint-green)]"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Company (Width: 2 cols) */}
          <div className="lg:col-span-2 xl:col-span-2">
            <h3 className="font-bricolage text-[16px] font-bold text-white tracking-wide">
              Company
            </h3>
            <ul className="mt-4 space-y-2.5">
              {[
                { name: "About Us", path: "/about" },
                { name: "How It Works", path: "/how-it-works" },
                { name: "For DSAs", path: "/for-dsas" },
                { name: "For Lenders", path: "/for-lenders" },
                { name: "Contact Us", path: "/contact" },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="font-jakarta text-[14px] text-[#9bb0a5] transition-colors duration-200 hover:text-[var(--mint-green)]"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Resources (Width: 2 cols) */}
          <div className="lg:col-span-2 xl:col-span-2">
            <h3 className="font-bricolage text-[16px] font-bold text-white tracking-wide">
              Resources
            </h3>
            <ul className="mt-4 space-y-2.5">
              {[
                { name: "EMI Calculator", path: "/#financial-calculators" },
                { name: "Eligibility Calculator", path: "/#financial-calculators" },
                { name: "ROI Calculator", path: "/#financial-calculators" },
                { name: "Stamp Duty Calculator", path: "/#financial-calculators" },
                { name: "Gold Loan Calculator", path: "/#financial-calculators" },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    onClick={(e) => {
                      if (item.path.includes("#") && window.location.pathname === "/") {
                        const hash = item.path.substring(item.path.indexOf("#"));
                        const element = document.querySelector(hash);
                        if (element) {
                          e.preventDefault();
                          element.scrollIntoView({ behavior: "smooth" });
                          window.history.pushState(null, "", item.path);
                        }
                      }
                    }}
                    className="font-jakarta text-[14px] text-[#9bb0a5] transition-colors duration-200 hover:text-[var(--mint-green)] cursor-pointer"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 5: Contact Us (Width: 3.5 cols) */}
          <div className="lg:col-span-2 xl:col-span-3">
            <h3 className="font-bricolage text-[16px] font-bold text-white tracking-wide">
              Registered Office
            </h3>
            <div className="mt-4 space-y-3.5">
              {/* Address */}
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 shrink-0 text-[var(--mint-green)] mt-0.5" />
                <p className="font-jakarta text-[13.5px] leading-[1.6] text-[#9bb0a5]">
                  Parthv Capverse Advisory Private Limited<br />
                  C-501, 5th Floor, Vipul Plaza, Sector 81, Amarnagar (Faridabad) - 121003, Haryana, India
                </p>
              </div>

              {/* Email */}
              <div className="flex items-center gap-3">
                <Mail className="h-4.5 w-4.5 shrink-0 text-[var(--mint-green)]" />
                <a
                  href="mailto:supportfinserv@gmail.com"
                  className="font-jakarta text-[14px] text-[#9bb0a5] transition-colors duration-200 hover:text-[var(--mint-green)]"
                >
                  supportfinserv@gmail.com
                </a>
              </div>
            </div>
          </div>

        </div>

        {/* ================= BOTTOM COPYRIGHT BAR ================= */}
        <div className="mt-12 border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="font-jakarta text-[13px] text-[#71857c]">
            © {new Date().getFullYear()} Parthv Capverse Advisory Private Limited (Finverse). All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy-policy"
              className="font-jakarta text-[13px] text-[#71857c] hover:text-[var(--mint-green)] transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms-and-condition"
              className="font-jakarta text-[13px] text-[#71857c] hover:text-[var(--mint-green)] transition-colors"
            >
              Terms & Conditions
            </Link>
          </div>
        </div>

      </div>

      {/* Left Side Floating "Apply Now" CTA Button */}
      <Link
        to="/contact"
        aria-label="Apply Now for Loans"
        className="group fixed bottom-6 left-6 z-40 flex items-center gap-2.5 rounded-full border border-white/20 bg-gradient-to-r from-[#008751] via-[#00a362] to-[#008751] px-5 py-3 text-white shadow-[0_10px_30px_rgba(0,135,81,0.4)] backdrop-blur-md transition-all duration-300 hover:scale-105 hover:shadow-[0_15px_35px_rgba(46,229,162,0.55)] animate-floatPulse sm:bottom-8 sm:left-8 sm:px-6 sm:py-3.5 outline-none focus:outline-none"
      >
        {/* Smooth Animated Pulse Ring Indicator */}
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--mint-yellow)] opacity-80" />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[var(--mint-yellow)]" />
        </span>

        <span className="font-jakarta text-[14px] sm:text-[15px] font-bold tracking-tight">
          Apply Now
        </span>

        <span className="font-jakarta text-[15px] transition-transform duration-200 group-hover:translate-x-1">
          →
        </span>
      </Link>

      {/* Fixed Floating Back to Top Button (Bottom Right) */}
      <button
        type="button"
        onClick={scrollToTop}
        className={`fixed bottom-6 right-6 z-40 flex h-11 w-11 items-center justify-center rounded-full border border-[var(--mint-green)]/40 bg-[#161c19]/85 text-[var(--mint-green)] shadow-[0_8px_30px_rgba(0,0,0,0.35)] backdrop-blur-xl transition-all duration-300 hover:scale-110 hover:border-[var(--mint-green)] hover:bg-[var(--mint-green)] hover:text-[#091328] outline-none focus:outline-none cursor-pointer sm:bottom-8 sm:right-8 sm:h-12 sm:w-12 ${showFloating
            ? "translate-y-0 opacity-100 pointer-events-auto"
            : "translate-y-4 opacity-0 pointer-events-none"
          }`}
        aria-label="Scroll back to top"
      >
        <ArrowUp size={20} strokeWidth={2.4} />
      </button>

      {/* Float & Pulse Glow Keyframes */}
      <style>{`
        @keyframes floatPulse {
          0%, 100% {
            transform: translateY(0px);
            box-shadow: 0 10px 30px rgba(0, 135, 81, 0.4);
          }
          50% {
            transform: translateY(-5px);
            box-shadow: 0 16px 36px rgba(46, 229, 162, 0.5);
          }
        }
        .animate-floatPulse {
          animation: floatPulse 3.2s ease-in-out infinite;
        }
      `}</style>
    </footer>
  );
}

export default Footer;